import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

interface LockInfo {
    lockedByUserId: string;
    lockedByName: string;
    lockedSince: string;
}

interface UseFaturaLockReturn {
    isLocked: boolean;
    isMyLock: boolean;
    lockInfo: LockInfo | null;
    acquiring: boolean;
    acquireLock: () => Promise<boolean>;
    releaseLock: () => Promise<void>;
}

const HEARTBEAT_INTERVAL_MS = 30_000; // 30s — expiry is 10 min, so 20 missed beats before stale

export function useFaturaLock(faturaId: number | null, userId: string | undefined): UseFaturaLockReturn {
    const [isLocked, setIsLocked] = useState(false);
    const [isMyLock, setIsMyLock] = useState(false);
    const [lockInfo, setLockInfo] = useState<LockInfo | null>(null);
    const [acquiring, setAcquiring] = useState(false);
    const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const lockAcquiredRef = useRef(false);
    const faturaIdRef = useRef(faturaId);
    const userIdRef = useRef(userId);

    useEffect(() => { faturaIdRef.current = faturaId; }, [faturaId]);
    useEffect(() => { userIdRef.current = userId; }, [userId]);

    const stopHeartbeat = useCallback(() => {
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
    }, []);

    const startHeartbeat = useCallback(() => {
        stopHeartbeat();
        if (!faturaId || !userId) return;

        heartbeatRef.current = setInterval(async () => {
            const { data, error } = await supabase.rpc('heartbeat_fatura_lock_v2', {
                p_fatura_id: faturaId,
                p_user_id: userId
            });

            if (error || !data) {
                console.warn('Heartbeat failed, attempting to re-acquire fatura lock...');
                const { data: reacquireData } = await supabase.rpc('acquire_fatura_lock_v2', {
                    p_fatura_id: faturaId,
                    p_user_id: userId
                });
                const result = reacquireData?.[0];
                if (!result?.success) {
                    stopHeartbeat();
                    setIsMyLock(false);
                    lockAcquiredRef.current = false;
                }
            }
        }, HEARTBEAT_INTERVAL_MS);
    }, [faturaId, userId, stopHeartbeat]);

    const acquireLock = useCallback(async (): Promise<boolean> => {
        if (!faturaId || !userId) return false;
        setAcquiring(true);

        try {
            const { data, error } = await supabase.rpc('acquire_fatura_lock_v2', {
                p_fatura_id: faturaId,
                p_user_id: userId
            });

            if (error) {
                console.error('Error acquiring fatura lock:', error);
                return false;
            }

            const result = data?.[0];
            if (!result) return false;

            if (result.success) {
                setIsLocked(true);
                setIsMyLock(true);
                setLockInfo({
                    lockedByUserId: result.locked_by_user_id,
                    lockedByName: result.locked_by_name || 'Usuário desconhecido',
                    lockedSince: result.locked_since
                });
                lockAcquiredRef.current = true;
                startHeartbeat();
                return true;
            } else {
                setIsLocked(true);
                setIsMyLock(false);
                setLockInfo({
                    lockedByUserId: result.locked_by_user_id,
                    lockedByName: result.locked_by_name || 'Usuário desconhecido',
                    lockedSince: result.locked_since
                });
                return false;
            }
        } catch (err) {
            console.error('Unexpected error acquiring fatura lock:', err);
            return false;
        } finally {
            setAcquiring(false);
        }
    }, [faturaId, userId, startHeartbeat]);

    const releaseLock = useCallback(async () => {
        stopHeartbeat();
        if (!faturaId || !userId) return;

        try {
            await supabase.rpc('release_fatura_lock_v2', {
                p_fatura_id: faturaId,
                p_user_id: userId
            });
        } catch (err) {
            console.error('Error releasing fatura lock:', err);
        } finally {
            setIsLocked(false);
            setIsMyLock(false);
            setLockInfo(null);
            lockAcquiredRef.current = false;
        }
    }, [faturaId, userId, stopHeartbeat]);

    const releaseViaFetch = useCallback((fId: number, uId: string) => {
        const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/release_fatura_lock_v2`;
        let accessToken = '';
        try {
            const storageKey = `sb-${import.meta.env.VITE_SUPABASE_URL?.split('//')[1]?.split('.')[0]}-auth-token`;
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const parsed = JSON.parse(stored);
                accessToken = parsed?.access_token || '';
            }
        } catch { /* ignore */ }

        try {
            fetch(url, {
                method: 'POST',
                keepalive: true,
                headers: {
                    'Content-Type': 'application/json',
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ p_fatura_id: fId, p_user_id: uId })
            }).catch(() => {});
        } catch { /* best effort */ }
    }, []);

    // Release on unmount / page unload
    useEffect(() => {
        const handleBeforeUnload = () => {
            const fId = faturaIdRef.current;
            const uId = userIdRef.current;
            if (lockAcquiredRef.current && fId && uId) {
                releaseViaFetch(fId, uId);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            stopHeartbeat();

            const fId = faturaIdRef.current;
            const uId = userIdRef.current;
            if (lockAcquiredRef.current && fId && uId) {
                supabase.rpc('release_fatura_lock_v2', {
                    p_fatura_id: fId,
                    p_user_id: uId
                }).then(({ error }) => {
                    if (error) console.error('Error releasing fatura lock on unmount:', error);
                });
                releaseViaFetch(fId, uId);
            }
            lockAcquiredRef.current = false;
        };
    }, [stopHeartbeat, releaseViaFetch]);

    return { isLocked, isMyLock, lockInfo, acquiring, acquireLock, releaseLock };
}
